import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Trash2, Mail, Phone, Calendar, Loader2, ArrowRight } from "lucide-react";
import { api } from "@/lib/api";
import { StatusPill } from "./OpsDashboard";

export default function OpsQuotes() {
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [converting, setConverting] = useState(null);
  const navigate = useNavigate();

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/quote");
      setQuotes(data);
    } catch {
      toast.error("Failed to load quotes");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const del = async (id) => {
    if (!window.confirm("Eliminar esta solicitud?")) return;
    try {
      await api.delete(`/quote/${id}`);
      setQuotes((q) => q.filter((x) => x.id !== id));
      toast.success("Eliminada");
    } catch {
      toast.error("No se pudo eliminar");
    }
  };

  const setStatus = async (id, status) => {
    try {
      await api.patch(`/quote/${id}`, { status });
      setQuotes((qs) => qs.map((q) => q.id === id ? { ...q, status } : q));
    } catch {
      toast.error("No se pudo actualizar");
    }
  };

  const convert = async (id) => {
    setConverting(id);
    try {
      const { data } = await api.post(`/orders/from-quote/${id}`);
      toast.success("Convertido a pedido");
      navigate(`/admin/ops/orders/${data.id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo convertir");
    } finally {
      setConverting(null);
    }
  };

  return (
    <div className="p-6 sm:p-10 max-w-6xl">
      <p className="text-[11px] uppercase tracking-[0.32em] text-[#D4AF37] font-semibold mb-2">Inbox</p>
      <h1 className="font-display text-3xl sm:text-4xl font-bold mb-2">Solicitudes de presupuesto</h1>
      <p className="text-white/60 text-sm mb-8">Convierte cualquier solicitud en un pedido para empezar a programarlo.</p>

      {loading ? (
        <div className="flex items-center gap-3 text-white/60 text-sm"><Loader2 className="w-4 h-4 animate-spin" /> Cargando…</div>
      ) : quotes.length === 0 ? (
        <div className="bg-white/[0.04] border border-white/10 rounded-2xl p-10 text-center text-white/55">
          Aún no hay solicitudes.
        </div>
      ) : (
        <div className="space-y-3" data-testid="ops-quote-list">
          {quotes.map((q) => (
            <div key={q.id} className="bg-white/[0.04] border border-white/10 rounded-2xl p-5 sm:p-6">
              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="font-display text-lg font-bold">{q.full_name}</h3>
                    <StatusPill value={q.status || "new"} />
                    <span className="px-2.5 py-0.5 rounded-full text-[10px] uppercase tracking-widest bg-[#D4AF37]/15 text-[#D4AF37] border border-[#D4AF37]/25 font-bold">
                      {q.service}
                    </span>
                  </div>
                  <div className="mt-2 flex flex-wrap gap-x-5 gap-y-1 text-sm text-white/65">
                    <span className="inline-flex items-center gap-1.5"><Mail className="w-3.5 h-3.5 text-[#D4AF37]" /> {q.email}</span>
                    <span className="inline-flex items-center gap-1.5"><Phone className="w-3.5 h-3.5 text-[#D4AF37]" /> {q.phone}</span>
                    <span className="inline-flex items-center gap-1.5"><Calendar className="w-3.5 h-3.5 text-[#D4AF37]" /> {new Date(q.created_at).toLocaleString()}</span>
                  </div>
                  {q.message && (
                    <p className="mt-3 text-sm text-white/75 whitespace-pre-wrap">{q.message}</p>
                  )}
                </div>
              </div>

              <div className="mt-5 flex flex-wrap gap-2 items-center pt-4 border-t border-white/5">
                <select
                  value={q.status || "new"}
                  onChange={(e) => setStatus(q.id, e.target.value)}
                  data-testid={`quote-status-${q.id}`}
                  className="bg-[#0A0A0A] border border-white/15 rounded-lg text-xs px-3 py-1.5 text-white/80 cursor-pointer hover:border-[#D4AF37]/50"
                >
                  <option value="new">Nuevo</option>
                  <option value="contacted">Contactado</option>
                  <option value="converted">Convertido</option>
                  <option value="archived">Archivado</option>
                </select>

                <button
                  onClick={() => convert(q.id)}
                  disabled={converting === q.id}
                  data-testid={`quote-convert-${q.id}`}
                  className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-lg text-xs font-bold bg-[#D4AF37] text-[#0A0A0A] hover:shadow-[0_0_18px_rgba(212,175,55,0.4)] transition-all disabled:opacity-60"
                >
                  {converting === q.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ArrowRight className="w-3.5 h-3.5" />}
                  Convertir a pedido
                </button>

                <a href={`tel:${q.phone}`} className="px-3.5 py-1.5 rounded-lg text-xs text-white/65 hover:text-[#D4AF37] hover:bg-white/5 transition-all inline-flex items-center gap-1.5">
                  <Phone className="w-3.5 h-3.5" /> Llamar
                </a>
                <a href={`mailto:${q.email}`} className="px-3.5 py-1.5 rounded-lg text-xs text-white/65 hover:text-[#D4AF37] hover:bg-white/5 transition-all inline-flex items-center gap-1.5">
                  <Mail className="w-3.5 h-3.5" /> Email
                </a>

                <button
                  onClick={() => del(q.id)}
                  data-testid={`quote-delete-${q.id}`}
                  className="ml-auto px-3 py-1.5 rounded-lg text-xs text-white/55 hover:text-red-400 hover:bg-red-500/10 transition-all inline-flex items-center gap-1.5"
                >
                  <Trash2 className="w-3.5 h-3.5" /> Eliminar
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
